/**
 * Layout B ("bureau du détective"), chosen 2026-10-08.
 *  - < 1024 px: full-screen single column (iOS parity, push chat → phone).
 *  - ≥ 1024 px: chat (or Home) on the left, copied phone in a device frame on the right,
 *    both visible at once; "Open phone" and the corner avatar overlay are hidden.
 *  - First launch: the cold open (incoming call, lock screen) shows alone, then hands off to the layout above.
 * Routes render inside <Outlet/>; on wide screens the phone column renders CopiedPhoneView directly and
 * the chat column renders ClientChatView at ONE stable position for `/play` and `/play/phone/*`, so a
 * phone deep link never remounts the chat (sequencer, scroll and open sheets survive).
 */
import { Suspense } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'motion/react';
import { Outlet, useLocation } from 'react-router-dom';
import { useColdOpen } from '../coldOpen';
import { useViewportClass } from './useViewportClass';

import { ClientChatView, ColdOpenScreen, CopiedPhoneView } from '../routeComponents';

export const PHONE_WIDTH = 402;
export const PHONE_HEIGHT = 874;

/** Settle used for the cold open → split hand-off (no overshoot: two large panels moving). */
const PANEL_SPRING: Transition = { type: 'spring', stiffness: 210, damping: 30, mass: 1 };
/** AppRootView: `.animation(.easeInOut(duration: 0.28), value: isShowingFirstLaunchColdOpen)`. */
const COLD_OPEN_FADE: Transition = { duration: 0.28, ease: [0.42, 0, 0.58, 1] };

export function DeviceFrame() {
  const viewport = useViewportClass();
  const location = useLocation();
  const coldOpen = useColdOpen();
  const reduceMotion = useReducedMotion() ?? false;
  const isChatRoute = location.pathname === '/play' || location.pathname.startsWith('/play/') || coldOpen.handingOff;

  if (viewport === 'mobile') {
    return (
      <div className="relative h-full w-full overflow-hidden bg-paper">
        {coldOpen.active || coldOpen.handingOff ? null : <Outlet />}
        <ColdOpenLayer active={coldOpen.active} />
      </div>
    );
  }

  if (viewport === 'tablet-portrait') {
    return (
      <div className="stage flex h-full w-full items-center justify-center">
        <div className="relative overflow-hidden rounded-[28px] bg-paper shadow-2xl" style={{ width: PHONE_WIDTH, height: `min(${PHONE_HEIGHT}px, 94vh)` }}>
          {coldOpen.active || coldOpen.handingOff ? null : <Outlet />}
          <ColdOpenLayer active={coldOpen.active} />
        </div>
      </div>
    );
  }

  // desktop / tablet-landscape: split. During the first-launch cold open only the phone shows, centred;
  // when it ends the phone glides to the right and the chat column comes in on the left.
  return (
    <div className="stage flex h-full w-full items-center justify-center gap-10 px-10">
      <AnimatePresence initial={false}>
        {coldOpen.active ? null : (
          <motion.div
            key="column"
            data-split-column="chat"
            className="h-[min(874px,92vh)] w-[clamp(360px,30vw,420px)] shrink-0 overflow-hidden rounded-[22px] bg-paper shadow-2xl"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -56, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={reduceMotion ? COLD_OPEN_FADE : { ...PANEL_SPRING, delay: 0.1 }}
          >
            {isChatRoute ? <ChatColumn /> : <Outlet />}
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        layout={!reduceMotion}
        transition={PANEL_SPRING}
        data-split-column="phone"
        className="shrink-0 rounded-[46px] bg-[#1a1a22] p-[10px] shadow-2xl"
        style={{ height: 'min(874px, 92vh)', aspectRatio: `${PHONE_WIDTH}/${PHONE_HEIGHT}` }}
      >
        <div className="relative h-full w-full overflow-hidden rounded-[38px] bg-black">
          {coldOpen.active ? null : (
            <Suspense fallback={null}>
              <CopiedPhoneView embedded />
            </Suspense>
          )}
          <ColdOpenLayer active={coldOpen.active} />
        </div>
      </motion.div>
    </div>
  );
}

/** The cold open sits above whatever comes next and fades out over it. */
function ColdOpenLayer({ active }: { active: boolean }) {
  return (
    <AnimatePresence initial={false}>
      {active ? (
        <motion.div key="cold-open" className="absolute inset-0 z-10" exit={{ opacity: 0 }} transition={COLD_OPEN_FADE}>
          <ColdOpenScreen />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}


/** Split chat column: same element type and position for `/play` and `/play/phone/*` (never remounted between them). */
function ChatColumn() {
  return (
    <Suspense fallback={null}>
      <ClientChatView />
    </Suspense>
  );
}
